import { Component, inject, Input, OnInit } from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { IconComponent } from '@shared/components/ui';
import { DateTimeUtility } from '@core/utils';
import { CalendarState, CalendarViewService } from '@core/services';

@UntilDestroy()
@Component({
  selector: 'app-calendar-view-header',
  imports: [IconComponent, TranslateModule],
  template: `
    <div class="calendar-header">
      <button (click)="navigatePrevious()" class="nav-button button-icon-only">
        <app-icon color="var(--black)" iconName="chevron-left"></app-icon>
      </button>

      <h1 class="month-title">{{ currentMonthYear }}</h1>

      <button (click)="navigateNext()" class="nav-button button-icon-only">
        <app-icon color="var(--black)" iconName="chevron-right"></app-icon>
      </button>
    </div>
  `,
  styles: [
    `
      .calendar-header {
        display: flex;
        align-items: center;
        justify-content: center;
        /*gap: 20px;*/

        .nav-button {
          padding: 8px;
          background: none;
          border: none;
          border-radius: 50%;
          cursor: pointer;
          transition: background-color 0.2s;

          &:hover {
            background-color: var(--gray-200);
          }

          svg {
            width: 20px;
            height: 20px;
          }
        }

        .month-title {
          font-size: 24px;
          font-weight: bold;
          color: var(--text-color-dark);
          margin: 0;
          min-width: 200px;
          text-align: center;
        }
      }

      @media (max-width: 768px) {
        .calendar-header .month-title {
          font-size: 20px;
          min-width: 150px;
        }
      }
    `,
  ],
})
export class CalendarViewHeaderComponent implements OnInit {
  @Input() initializeService = true; // Whether this component should initialize the service
  @Input() initialDate?: Date;
  @Input() fixedMonthNumber?: number;
  @Input() fixedYear?: number;
  currentMonthYear = '';
  private readonly _translateService = inject(TranslateService);
  private readonly _calendarService = inject(CalendarViewService);

  ngOnInit(): void {
    // Initialize service if this component should do it
    if (this.initializeService) {
      this._calendarService.initialize(this.initialDate, this.fixedMonthNumber, this.fixedYear);
    }

    // Subscribe to state changes
    this._calendarService.currentState$.pipe(untilDestroyed(this)).subscribe({
      next: (state: CalendarState) => {
        this.currentMonthYear = this._buildMonthYear(state.currentDate);
      },
    });
  }

  navigatePrevious(): void {
    this._calendarService.navigateMonth(-1);
  }

  navigateNext(): void {
    this._calendarService.navigateMonth(1);
  }

  private _buildMonthYear(date: Date): string {
    const monthName = DateTimeUtility.getMonthName(date.getMonth() + 1, this._translateService);
    return `${monthName} ${date.getFullYear()}`;
  }
}
